import { ReactNode, useContext, useEffect, useRef, useState } from 'react';
import { useQuery } from 'react-query';
import { api } from '../../services/api';
import type { EventDetail } from '../../lib/events';
import { Panel, PanelHeader, PanelBody } from '../ui/Panel';
import { ShowAllTabs, useEventContent } from './pageContext';
import Button from '../ui/Button';
import Modal from '../ui/Modal';
import Pill, { StatusMark } from '../ui/Pill';
import { EmptyState, ErrorNotice, InfoNotice, SkeletonRows } from '../ui/States';
import { errorMessage, money, toCents } from '../../lib/programs';
import {
  CONFIRMATION_LABEL,
  Confirmation,
  PartRole,
  ROLE_LABEL,
  Speaker,
  field,
  missingSomething,
  nextDue,
  plural,
  shortDate,
  stack,
  stillToGet,
  timeRange,
  useAgenda,
  useAgendaWrite,
} from './agendaTypes';
import { FOLLOW_UP_EVENT } from './AgendaPanel';
import { dayLabel } from '../../lib/events';
import { confirmAction } from '../ui/Confirm';
import { headRow, th, td, em, num } from './logistics';

/** Rows shown before "Show the other N speakers". */
const FIRST_ROWS = 10;

function Field({
  title,
  hint,
  children,
  wide = false,
}: {
  title: string;
  hint?: string;
  children: ReactNode;
  wide?: boolean;
}) {
  return (
    <div className={wide ? field.wide : ''}>
      <label className="block">
        <span className={field.label}>{title}</span>
        {children}
      </label>
      {hint ? <p className={field.hint}>{hint}</p> : null}
    </div>
  );
}

function Check({
  checked,
  onChange,
  children,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  children: ReactNode;
}) {
  return (
    <label className={field.check}>
      <input type="checkbox" className={field.box} checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span>{children}</span>
    </label>
  );
}

/** A folded group of fields in the Edit speaker dialog. */
function Fold({ title, children, open = false }: { title: string; children: ReactNode; open?: boolean }) {
  return (
    <details className={field.fold} open={open}>
      <summary className={field.summary}>{title}</summary>
      <div className="pb-4">{children}</div>
    </details>
  );
}

function AddSpeaker({ eventId, onClose }: { eventId: string; onClose: () => void }) {
  const agenda = useAgenda(eventId);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [title, setTitle] = useState('');
  const [affiliation, setAffiliation] = useState('');
  const [sessionId, setSessionId] = useState('');
  const [role, setRole] = useState<PartRole>('speaker');
  const add = useAgendaWrite(eventId, () =>
    api.post(`/events/${eventId}/agenda/speakers`, {
      ...(email.trim() ? { email: email.trim(), name: name.trim() } : { newContact: { name: name.trim() } }),
      title,
      affiliation,
      sessionId: sessionId || undefined,
      role,
    }),
  );
  const ready = Boolean(name.trim() || email.trim());
  return (
    <Modal
      title="Add a speaker"
      why="With an email, the member or contact already on file is used. Without one, a new contact (kind speaker) is made; it can be given an email later on the Contacts page."
      onClose={onClose}
      width={620}
      footer={
        <>
          <Button
            variant="primary"
            disabled={add.isLoading || !ready}
            onClick={() => add.mutate(undefined, { onSuccess: onClose })}
          >
            Add the speaker
          </Button>
          <Button onClick={onClose}>Cancel</Button>
          {!ready ? <span className="text-small text-ink-3">Give a name or an email.</span> : null}
        </>
      }
    >
      {add.isError ? (
        <div className="mb-4">
          <ErrorNotice>{errorMessage(add.error, 'Could not add the speaker')}</ErrorNotice>
        </div>
      ) : null}
      <div className={field.grid}>
        <Field title="Name">
          <input className={field.input} value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field title="Email" hint="Optional, if you give a name.">
          <input className={field.input} type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
        <Field title="Job title">
          <input className={field.input} value={title} onChange={(e) => setTitle(e.target.value)} />
        </Field>
        <Field title="Affiliation" hint="Company or university.">
          <input className={field.input} value={affiliation} onChange={(e) => setAffiliation(e.target.value)} />
        </Field>
        <Field title="Session" hint="Optional.">
          <select className={field.input} value={sessionId} onChange={(e) => setSessionId(e.target.value)}>
            <option value="">None yet</option>
            {(agenda.data?.sessions ?? []).map((s) => (
              <option key={s.id} value={s.id}>
                {dayLabel(s.day)} {timeRange(s)} {s.title}
              </option>
            ))}
          </select>
        </Field>
        <Field title="Role">
          <select className={field.input} value={role} onChange={(e) => setRole(e.target.value as PartRole)}>
            {Object.entries(ROLE_LABEL).map(([k, l]) => (
              <option key={k} value={k}>
                {l}
              </option>
            ))}
          </select>
        </Field>
      </div>
    </Modal>
  );
}

function EditSpeaker({
  eventId,
  sp,
  full,
  onClose,
}: {
  eventId: string;
  sp: Speaker;
  full: boolean;
  onClose: () => void;
}) {
  const [f, setF] = useState({
    displayName: sp.displayName ?? '',
    title: sp.title ?? '',
    affiliation: sp.affiliation ?? '',
    bio: sp.bio ?? '',
    bioFr: sp.bioFr ?? '',
    photo: sp.photo ?? '',
    confirmation: sp.confirmation,
    format: sp.format ?? '',
    hotelNeeded: sp.hotelNeeded === null || sp.hotelNeeded === undefined ? '' : sp.hotelNeeded ? 'yes' : 'no',
    invited: Boolean(sp.invitedAt),
    approvalSent: Boolean(sp.approvalSentAt),
    pheedloop: Boolean(sp.pheedloopAt),
    honorarium: sp.honorarium ?? 'none',
    honorarium$: sp.honorariumCents ? String(sp.honorariumCents / 100) : '',
    notes: sp.notes ?? '',
    speakerType: sp.speakerType ?? '',
    payeeType: sp.payeeType ?? '',
    draftDeck: Boolean(sp.deliverables.draftDeckAt),
    finalDeck: Boolean(sp.deliverables.finalDeckAt),
    glossary: Boolean(sp.deliverables.glossaryAt),
    prepMeeting: Boolean(sp.deliverables.prepMeetingAt),
    deckLink: sp.deliverables.deckLink ?? '',
    glossaryLink: sp.deliverables.glossaryLink ?? '',
    dinnerRsvp: sp.deliverables.dinnerRsvp ?? '',
    consent: sp.deliverables.consentToRecord === true ? 'yes' : sp.deliverables.consentToRecord === false ? 'no' : '',
    dietary: sp.commitment?.dietary ?? '',
    schedule: sp.commitment?.schedule ?? '',
    arrival: sp.commitment?.arrival ?? '',
    departure: sp.commitment?.departure ?? '',
    invitationNotes: sp.engagement?.invitationNotes ?? '',
    timing: sp.engagement?.timing ?? '',
    protocol: sp.engagement?.protocol ?? '',
    honorariumAgreed: Boolean(sp.engagement?.honorariumAgreedAt),
    route: sp.engagement?.route ?? '',
    routeStatus: sp.engagement?.routeStatus ?? '',
  });
  const agenda = useAgenda(eventId);
  const types = agenda.data?.speakerTypes;
  const payees = agenda.data?.payeeTypes;
  const set = (k: keyof typeof f, v: string | boolean) => setF((x) => ({ ...x, [k]: v }));
  const save = useAgendaWrite(eventId, () =>
    api.patch(`/events/agenda/speakers/${sp.id}`, {
      displayName: f.displayName,
      title: f.title,
      affiliation: f.affiliation,
      bio: f.bio,
      bioFr: f.bioFr,
      photo: f.photo,
      confirmation: f.confirmation,
      format: f.format || null,
      invited: f.invited,
      approvalSent: f.approvalSent,
      pheedloop: f.pheedloop,
      speakerType: f.speakerType || null,
      deliverables: {
        draftDeck: f.draftDeck,
        finalDeck: f.finalDeck,
        glossary: f.glossary,
        prepMeeting: f.prepMeeting,
        deckLink: f.deckLink,
        glossaryLink: f.glossaryLink,
        dinnerRsvp: f.dinnerRsvp || null,
        consentToRecord: f.consent === '' ? null : f.consent === 'yes',
      },
      ...(full
        ? {
            hotelNeeded: f.hotelNeeded === '' ? null : f.hotelNeeded === 'yes',
            honorarium: f.honorarium,
            honorariumCents: toCents(f.honorarium$),
            notes: f.notes,
            payeeType: f.payeeType || null,
            commitment: { dietary: f.dietary, schedule: f.schedule, arrival: f.arrival, departure: f.departure },
            engagement: {
              invitationNotes: f.invitationNotes,
              timing: f.timing,
              protocol: f.protocol,
              honorariumAgreed: f.honorariumAgreed,
              route: f.route || null,
              routeStatus: f.routeStatus || null,
            },
          }
        : {}),
    }),
  );
  const dueNote = (key: string, sep = ' · ') => {
    const d = sp.deliverableItems.find((i) => i.key === key);
    return d?.due ? `${sep}${d.overdue && !d.done ? 'was due' : 'due'} ${shortDate(d.due)}` : '';
  };
  const remove = useAgendaWrite(eventId, () => api.delete(`/events/agenda/speakers/${sp.id}`));
  return (
    <Modal
      title={`Edit speaker: ${sp.name}`}
      why="Grouped by what you're doing. Only the first group is open; the rest unfold when needed."
      onClose={onClose}
      width={720}
      footer={
        <>
          <Button
            variant="primary"
            disabled={save.isLoading}
            onClick={() => save.mutate(undefined, { onSuccess: onClose })}
          >
            Save
          </Button>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="destructive"
            className="ml-auto"
            onClick={() => {
              void confirmAction(
                `Remove ${sp.name} from the event? Their session parts go too; a ledger entry stays.`,
                'Remove from event',
              ).then((ok) => ok && remove.mutate(undefined, { onSuccess: onClose }));
            }}
          >
            Remove from event
          </Button>
        </>
      }
    >
      {save.isError || remove.isError ? (
        <div className="mb-4">
          <ErrorNotice>{errorMessage(save.error || remove.error, 'Could not save')}</ErrorNotice>
        </div>
      ) : null}
      <div className={field.grid}>
        <Field title="Participation">
          <select
            className={field.input}
            value={f.confirmation}
            onChange={(e) => set('confirmation', e.target.value as Confirmation)}
          >
            {Object.entries(CONFIRMATION_LABEL).map(([k, l]) => (
              <option key={k} value={k}>
                {l}
              </option>
            ))}
          </select>
        </Field>
        <Field title="Format">
          <select className={field.input} value={f.format} onChange={(e) => set('format', e.target.value)}>
            <option value="">To be confirmed</option>
            <option value="in_person">In person</option>
            <option value="virtual">Online</option>
          </select>
        </Field>
        <Field title="Type of speaker">
          <select className={field.input} value={f.speakerType} onChange={(e) => set('speakerType', e.target.value)}>
            <option value="">Not set</option>
            {types
              ? Object.entries(types).map(([k, l]) => (
                  <option key={k} value={k}>
                    {l}
                  </option>
                ))
              : null}
          </select>
        </Field>
        <Field title="Name as printed" hint="With Dr. and credentials, for example.">
          <input className={field.input} value={f.displayName} onChange={(e) => set('displayName', e.target.value)} />
        </Field>
        {sp.onGrant || sp.transfersCovered ? (
          <div className={`${field.wide} text-small text-ink-2`}>
            {sp.onGrant ? <p>PI or Co-I on the grant: no honorarium.</p> : null}
            {sp.transfersCovered ? <p>External speaker: home-city airport transfers are covered.</p> : null}
          </div>
        ) : null}
        <fieldset className={field.wide}>
          <legend className={field.label}>Received</legend>
          <div className="grid grid-cols-2 gap-x-4 gap-y-[6px] max-md:grid-cols-1">
            <span className={field.check}>
              <StatusMark mark={f.bio.trim() ? 'done' : 'open'} />
              Bio{f.bio.trim() ? '' : ': not in yet (in the group below)'}
            </span>
            <span className={field.check}>
              <StatusMark mark={f.photo.trim() ? 'done' : 'open'} />
              Photo{f.photo.trim() ? '' : ': not in yet (in the group below)'}
            </span>
            <Check checked={f.invited} onChange={(v) => set('invited', v)}>
              Invited
            </Check>
            <Check checked={f.approvalSent} onChange={(v) => set('approvalSent', v)}>
              Approval email sent
            </Check>
            <Check checked={f.pheedloop} onChange={(v) => set('pheedloop', v)}>
              PheedLoop profile
            </Check>
            <Check checked={f.draftDeck} onChange={(v) => set('draftDeck', v)}>
              Draft slides{dueNote('draft_deck')}
            </Check>
            <Check checked={f.finalDeck} onChange={(v) => set('finalDeck', v)}>
              Final slides{dueNote('final_deck')}
            </Check>
          </div>
        </fieldset>
      </div>
      <div className="mt-5">
        <Fold title="Title, affiliation, bio and photo">
          <div className={field.grid}>
            <Field title="Job title">
              <input className={field.input} value={f.title} onChange={(e) => set('title', e.target.value)} />
            </Field>
            <Field title="Affiliation">
              <input
                className={field.input}
                value={f.affiliation}
                onChange={(e) => set('affiliation', e.target.value)}
              />
            </Field>
            <Field title="Bio in English">
              <textarea className={field.input} rows={5} value={f.bio} onChange={(e) => set('bio', e.target.value)} />
            </Field>
            <Field title="Bio in French">
              <textarea
                className={field.input}
                rows={5}
                value={f.bioFr}
                onChange={(e) => set('bioFr', e.target.value)}
              />
            </Field>
            <Field title="Photo" hint="A link or the file name on the shared drive (no uploads)." wide>
              <input className={field.input} value={f.photo} onChange={(e) => set('photo', e.target.value)} />
            </Field>
          </div>
        </Fold>
        <Fold title="Slides, glossary, dinner and recording consent">
          <div className={field.grid}>
            <Field title="Slides" hint="A link on the shared drive (no uploads)." wide>
              <input className={field.input} value={f.deckLink} onChange={(e) => set('deckLink', e.target.value)} />
            </Field>
            <Check checked={f.glossary} onChange={(v) => set('glossary', v)}>
              Glossary received{dueNote('glossary')}
            </Check>
            <Check checked={f.prepMeeting} onChange={(v) => set('prepMeeting', v)}>
              Prep meeting held{dueNote('prep_meeting')}
            </Check>
            <Field title="Glossary" hint="A link, for the interpreters." wide>
              <input
                className={field.input}
                value={f.glossaryLink}
                onChange={(e) => set('glossaryLink', e.target.value)}
              />
            </Field>
            <Field title={`Dinner${dueNote('dinner')}`}>
              <select className={field.input} value={f.dinnerRsvp} onChange={(e) => set('dinnerRsvp', e.target.value)}>
                <option value="">No answer yet</option>
                <option value="yes">Coming</option>
                <option value="no">Not coming</option>
              </select>
            </Field>
            <Field title={`Consent to be recorded${dueNote('consent')}`}>
              <select className={field.input} value={f.consent} onChange={(e) => set('consent', e.target.value)}>
                <option value="">No answer yet</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            </Field>
          </div>
        </Fold>
        {full ? (
          <Fold title="Arrival, departure and dietary needs (admins)">
            <p className="text-small text-ink-3 mb-3">Their details, when they have no form response.</p>
            <div className={field.grid}>
              <Field title="Dietary needs">
                <input className={field.input} value={f.dietary} onChange={(e) => set('dietary', e.target.value)} />
              </Field>
              <Field title={`Arrival${dueNote('arrival')}`} hint="For example: Sunday 6 p.m., Pearson.">
                <input className={field.input} value={f.arrival} onChange={(e) => set('arrival', e.target.value)} />
              </Field>
              <Field title="Departure">
                <input className={field.input} value={f.departure} onChange={(e) => set('departure', e.target.value)} />
              </Field>
              <Field title="Schedule" hint="The days they attend.">
                <input className={field.input} value={f.schedule} onChange={(e) => set('schedule', e.target.value)} />
              </Field>
            </div>
          </Fold>
        ) : null}
        {full && f.speakerType === 'elder' ? (
          <Fold title="Elder or Knowledge Keeper">
            <div className={field.grid}>
              <Field title="Invitation" hint="Who asked, and how." wide>
                <textarea
                  className={field.input}
                  rows={2}
                  value={f.invitationNotes}
                  onChange={(e) => set('invitationNotes', e.target.value)}
                />
              </Field>
              <Field title="Timing to respect" hint="For example: not during harvest season.">
                <input className={field.input} value={f.timing} onChange={(e) => set('timing', e.target.value)} />
              </Field>
              <Field title="Protocol items" hint="For example: tobacco offering.">
                <input className={field.input} value={f.protocol} onChange={(e) => set('protocol', e.target.value)} />
              </Field>
              <div className={field.wide}>
                <Check checked={f.honorariumAgreed} onChange={(v) => set('honorariumAgreed', v)}>
                  Honorarium agreed with the NPI
                </Check>
              </div>
              <Field title="Paid as">
                <select className={field.input} value={f.route} onChange={(e) => set('route', e.target.value)}>
                  <option value="">Not decided</option>
                  <option value="honorarium">Honorarium</option>
                  <option value="gift">Gift (participant fee)</option>
                </select>
              </Field>
              <Field title="Payment">
                <select
                  className={field.input}
                  value={f.routeStatus}
                  onChange={(e) => set('routeStatus', e.target.value)}
                >
                  <option value="">Not started</option>
                  <option value="in_progress">In progress</option>
                  <option value="done">Paid or given</option>
                </select>
              </Field>
            </div>
          </Fold>
        ) : null}
        {full ? (
          <Fold title="Hotel, travel and honorarium (admins)">
            <div className={field.grid}>
              <Field title="Hotel or travel needed" hint="When they are not on the invitee list.">
                <select
                  className={field.input}
                  value={f.hotelNeeded}
                  onChange={(e) => set('hotelNeeded', e.target.value)}
                >
                  <option value="">To be confirmed</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </Field>
              <Field title="Honorarium">
                <select
                  className={field.input}
                  value={f.honorarium}
                  onChange={(e) => set('honorarium', e.target.value)}
                >
                  <option value="none">None</option>
                  <option value="paid" disabled={sp.onGrant}>
                    Paid
                  </option>
                  <option value="declined">Declined</option>
                </select>
              </Field>
              <Field title="How they are paid">
                <select className={field.input} value={f.payeeType} onChange={(e) => set('payeeType', e.target.value)}>
                  <option value="">Not set</option>
                  {payees
                    ? Object.entries(payees).map(([k, l]) => (
                        <option key={k} value={k}>
                          {l}
                        </option>
                      ))
                    : null}
                </select>
              </Field>
              <Field title="Honorarium amount ($)">
                <input
                  className={`${field.input} tabular`}
                  inputMode="decimal"
                  value={f.honorarium$}
                  onChange={(e) => set('honorarium$', e.target.value)}
                />
              </Field>
              <Field title="Notes (admins only)" wide>
                <textarea
                  className={field.input}
                  rows={2}
                  value={f.notes}
                  onChange={(e) => set('notes', e.target.value)}
                />
              </Field>
              <p className={`${field.wide} text-small text-ink-3`}>
                A paid honorarium becomes an entry owed in the payments ledger once the speaker is confirmed. PIs and
                Co-Is on the grant are not paid. Later amount changes are made on the Payments page.
              </p>
            </div>
          </Fold>
        ) : null}
      </div>
    </Modal>
  );
}

const STATUS_TONE = { confirmed: 'active', declined: 'inactive', tbc: 'planned' } as const;
const FORMAT_TEXT = (f: Speaker['format']) =>
  f === 'in_person' ? 'In person' : f === 'virtual' ? 'Online' : 'Format to be confirmed';

function travelText(s: Speaker): string {
  if (s.travel) {
    if (s.travel.hotel)
      return ['Hotel', s.travel.nights ? plural(s.travel.nights, 'night') : null, s.travel.travelMode]
        .filter(Boolean)
        .join(', ');
    return s.travel.hotel === false ? 'No hotel' : 'Not asked';
  }
  return s.hotelNeeded === true ? 'Hotel needed' : s.hotelNeeded === false ? 'No hotel' : 'To be confirmed';
}

/** The speaker tracker: statuses per speaker with what is still missing. */
export default function SpeakersPanel({ eventId, canEdit }: { eventId: string; canEdit: boolean }) {
  const agenda = useAgenda(eventId);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<Speaker | null>(null);
  const [onlyMissing, setOnlyMissing] = useState(false);
  const [everyone, setEveryone] = useState(false);
  const section = useRef<HTMLDivElement>(null);
  const invite = useAgendaWrite(eventId, (id: string) => api.post(`/events/agenda/speakers/${id}/invite`, {}));
  const full = Boolean(agenda.data?.canSeeNeeds);
  const showAll = useContext(ShowAllTabs);
  const event = useQuery<EventDetail>(['event', eventId], async () => (await api.get(`/events/${eventId}`)).data.data);
  const content = useEventContent(eventId);
  // An event that is over is a record: the planning columns (status, travel, still to get) only matter ahead of it.
  const planning = showAll || !content.data || !event.data || new Date(event.data.event.endsAt) >= new Date();
  const all = agenda.data?.speakers ?? [];
  const showHonorarium = full && all.some((s) => s.honorarium === 'paid' || s.honorarium === 'declined');
  const withMissing = all.filter(missingSomething).length;
  const filtered = all.filter((s) => !onlyMissing || missingSomething(s));
  const speakers = everyone ? filtered : filtered.slice(0, FIRST_ROWS);

  useEffect(() => {
    const onFollowUp = () => {
      setOnlyMissing(true);
      section.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };
    window.addEventListener(FOLLOW_UP_EVENT, onFollowUp);
    return () => window.removeEventListener(FOLLOW_UP_EVENT, onFollowUp);
  }, []);

  const note = agenda.data
    ? planning && all.length
      ? withMissing === all.length && all.length > 1
        ? `${all.length}, all with something missing`
        : `${all.length}, ${withMissing} with something missing`
      : `${all.length}`
    : undefined;

  return (
    <Panel>
      <div ref={section} id="speakers" className="scroll-mt-6" />
      <PanelHeader title="Speakers" note={note}>
        {planning && all.length ? (
          <label className={field.check}>
            <input
              type="checkbox"
              className={field.box}
              checked={onlyMissing}
              onChange={(e) => setOnlyMissing(e.target.checked)}
            />
            Only those missing something
          </label>
        ) : null}
        {canEdit ? <Button onClick={() => setAdding(true)}>Add a speaker</Button> : null}
      </PanelHeader>
      <PanelBody className="space-y-3">
        {agenda.isLoading ? <SkeletonRows rows={6} /> : null}
        {agenda.isError ? <ErrorNotice>{errorMessage(agenda.error, 'Could not load the speakers')}</ErrorNotice> : null}
        {invite.isError ? (
          <ErrorNotice>{errorMessage(invite.error, 'Could not add to the invitees')}</ErrorNotice>
        ) : null}
        {!full && agenda.data ? (
          <InfoNotice>Hotel and travel needs, notes and honoraria are shown to admins only.</InfoNotice>
        ) : null}
        {agenda.data && !filtered.length ? (
          <EmptyState title={onlyMissing ? 'Nothing missing' : 'No speakers yet'}>
            {onlyMissing
              ? 'Every speaker has what they need.'
              : 'Add speakers here, or import the speakers and sessions tab of the planning sheet.'}
          </EmptyState>
        ) : null}
        {speakers.length ? (
          <div className="overflow-x-auto max-md:overflow-visible">
            <table className={`${stack.table} ${planning ? 'min-w-[960px]' : ''} max-md:min-w-0`}>
              <thead className={stack.head}>
                <tr className={headRow}>
                  <th className={`${th} w-[19%]`}>Speaker</th>
                  <th className={`${th} w-[16%]`}>Sessions</th>
                  {planning ? (
                    <>
                      <th className={`${th} w-[13%]`}>Status</th>
                      <th className={`${th} w-[12%] min-w-[130px]`}>Travel</th>
                      <th className={th}>Still to get</th>
                      <th className={`${th} ${num} whitespace-nowrap`}>Next due</th>
                    </>
                  ) : null}
                  {showHonorarium ? <th className={`${th} ${num}`}>Honorarium</th> : null}
                  {canEdit ? (
                    <th className={th}>
                      <span className="sr-only">Edit</span>
                    </th>
                  ) : null}
                </tr>
              </thead>
              <tbody className={stack.body}>
                {speakers.map((s) => {
                  const due = nextDue(s.deliverableItems);
                  const declined = s.confirmation === 'declined';
                  const titleLine = [s.title, s.affiliation].filter(Boolean).join(' · ');
                  return (
                    <tr key={s.id} className={`${stack.row} hover:bg-selected ${declined ? '[&_td]:text-ink-3' : ''}`}>
                      <td className={`${td} ${stack.cell}`}>
                        <span className="font-semibold">{s.name}</span>
                        <span className={em}>
                          {[
                            s.speakerType && agenda.data?.speakerTypes ? agenda.data.speakerTypes[s.speakerType] : null,
                            titleLine || 'no title',
                            s.email,
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                        </span>
                      </td>
                      <td className={`${td} ${stack.cell}`} data-label="Sessions">
                        {s.sessions.length ? (
                          s.sessions.map((x) => (
                            <div key={x.partId}>
                              {x.title} <span className="text-ink-3">({ROLE_LABEL[x.role].toLowerCase()})</span>
                            </div>
                          ))
                        ) : (
                          <span className="text-ink-3">No session yet</span>
                        )}
                      </td>
                      {planning ? (
                        <>
                          <td className={`${td} ${stack.cell}`} data-label="Status">
                            <Pill tone={STATUS_TONE[s.confirmation]}>{CONFIRMATION_LABEL[s.confirmation]}</Pill>
                            <span className={em}>{FORMAT_TEXT(s.format)}</span>
                          </td>
                          <td className={`${td} ${stack.cell}`} data-label="Travel">
                            {full ? travelText(s) : <span className="text-ink-3">–</span>}
                            <span className={em}>
                              {!s.invitee
                                ? 'Not invited'
                                : s.inCoreForm
                                  ? 'In the core form'
                                  : s.inCoreForm === false
                                    ? 'Not in the core form'
                                    : 'No core form answer yet'}
                            </span>
                            {canEdit && !s.invitee ? (
                              <button
                                type="button"
                                className="text-small text-accent underline underline-offset-[3px] hover:text-ink"
                                disabled={invite.isLoading}
                                onClick={() => invite.mutate(s.id)}
                              >
                                Add to invitees
                              </button>
                            ) : null}
                          </td>
                          <td className={`${td} ${stack.cell}`} data-label="Still to get">
                            {missingSomething(s) ? stillToGet(s) : <span className="text-ink-3">Nothing</span>}
                          </td>
                          <td
                            className={`${td} ${stack.cell} ${num} whitespace-nowrap max-md:text-left`}
                            data-label="Next due"
                          >
                            {due?.due ? (
                              due.overdue ? (
                                <span className="text-warning font-semibold">was due {shortDate(due.due)}</span>
                              ) : (
                                shortDate(due.due)
                              )
                            ) : (
                              <span className="text-ink-3">–</span>
                            )}
                          </td>
                        </>
                      ) : null}
                      {showHonorarium ? (
                        <td className={`${td} ${stack.cell} ${num} max-md:text-left`} data-label="Honorarium">
                          {s.honorarium === 'paid' ? (
                            <>
                              {money(s.honorariumCents)}
                              {s.paymentEntryId ? <span className={em}>in the ledger</span> : null}
                            </>
                          ) : s.honorarium === 'declined' ? (
                            'Declined'
                          ) : (
                            <span className="text-ink-3">–</span>
                          )}
                        </td>
                      ) : null}
                      {canEdit ? (
                        <td className={`${td} ${stack.acts} text-right`}>
                          <Button size="sm" aria-label={`Edit ${s.name}`} onClick={() => setEditing(s)}>
                            Edit
                          </Button>
                        </td>
                      ) : null}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : null}
        {filtered.length > FIRST_ROWS ? (
          <p className="text-[15px] leading-[22px]">
            <Button variant="quiet" className="!px-0" onClick={() => setEveryone((x) => !x)}>
              {everyone ? 'Show the first 10 only' : `Show the other ${filtered.length - FIRST_ROWS} speakers`}
            </Button>
          </p>
        ) : null}
        {planning && speakers.length ? (
          <p className="text-small text-ink-3">
            &ldquo;Still to get&rdquo; lists what each speaker hasn&rsquo;t sent yet; an item stays until it&rsquo;s in.
            {showHonorarium ? ' Honoraria are shown to admins once anyone is paid or declines.' : ''}
          </p>
        ) : null}
      </PanelBody>
      {adding ? <AddSpeaker eventId={eventId} onClose={() => setAdding(false)} /> : null}
      {editing ? <EditSpeaker eventId={eventId} sp={editing} full={full} onClose={() => setEditing(null)} /> : null}
    </Panel>
  );
}
